/**
 * Setup Centre / first-run experience service.
 *
 * READ-ONLY over existing production data. Nothing here writes business data —
 * it only derives setup progress, a readiness score, recommendations and
 * feature-adoption signals from records that already exist in the account.
 *
 * Fully additive: to add a new signal, extend `SetupSignals`, populate it in
 * `loadSetupSignals` and reference it from a task/category below.
 */
import { supabase } from "@/integrations/supabase/client";

export interface SetupSignals {
  // Business details
  hasCompanyName: boolean;
  hasCompanyAddress: boolean;
  hasContactNumber: boolean;
  hasEmailAddress: boolean;
  hasLogo: boolean;
  hasVatDecision: boolean;
  // Finance
  hasBankDetails: boolean;
  hasPaymentTerms: boolean;
  accountingConnected: boolean;
  accountingProvider: string | null;
  // Fleet & people
  driverCount: number;
  vehicleCount: number;
  teamMemberCount: number;
  driverAppDevices: number;
  // Customers & work
  customerCount: number;
  jobCount: number;
  deliveredJobCount: number;
  invoiceCount: number;
  quoteCount: number;
  appraisalCount: number;
  // Automation state — derived exclusively from Company Settings
  autoInvoicingEnabled: boolean;
  autoEmailInvoice: boolean;
  autoEmailPoc: boolean;
  autoEmailPod: boolean;
  accountingSyncEnabled: boolean;
  /** Number of Company Settings automation toggles available. */
  automationTotal: number;
  /** Number of Company Settings automation toggles currently switched on. */
  automationEnabled: number;
  messageCount: number;
  dailyCheckCount: number;
  trackingSharedCount: number;
}

export const EMPTY_SIGNALS: SetupSignals = {
  hasCompanyName: false,
  hasCompanyAddress: false,
  hasContactNumber: false,
  hasEmailAddress: false,
  hasLogo: false,
  hasVatDecision: false,
  hasBankDetails: false,
  hasPaymentTerms: false,
  accountingConnected: false,
  accountingProvider: null,
  driverCount: 0,
  vehicleCount: 0,
  teamMemberCount: 0,
  driverAppDevices: 0,
  customerCount: 0,
  jobCount: 0,
  deliveredJobCount: 0,
  invoiceCount: 0,
  quoteCount: 0,
  appraisalCount: 0,
  autoInvoicingEnabled: false,
  autoEmailInvoice: false,
  autoEmailPoc: false,
  autoEmailPod: false,
  accountingSyncEnabled: false,
  automationTotal: 0,
  automationEnabled: 0,
  messageCount: 0,
  dailyCheckCount: 0,
  trackingSharedCount: 0,
};

const filled = (v: unknown) => typeof v === "string" && v.trim().length > 0;

const countOf = (res: { count: number | null } | { count?: number | null }) =>
  (res as { count?: number | null })?.count ?? 0;

export interface SetupPreferences {
  enabled: boolean;
  dismissed: boolean;
  wizardCompleted: boolean;
  tourCompleted: boolean;
  milestones: string[];
}

export interface SetupSnapshot {
  signals: SetupSignals;
  preferences: SetupPreferences;
}

export async function loadSetupSnapshot(companyId: string): Promise<SetupSnapshot> {
  const head = { count: "exact" as const, head: true };

  const [
    settingsRes,
    driversRes,
    vehiclesRes,
    membersRes,
    devicesRes,
    customersRes,
    jobsRes,
    quotesRes,
    appraisalsRes,
    messagesRes,
    checksRes,
    accountingRes,
  ] = await Promise.all([
    supabase.from("company_settings").select("*").eq("company_id", companyId).maybeSingle(),
    supabase.from("drivers").select("id", head).eq("company_id", companyId),
    supabase.from("vehicles").select("id", head).eq("company_id", companyId),
    supabase.from("company_members").select("id", head).eq("company_id", companyId),
    supabase.from("device_tokens").select("id", head).eq("company_id", companyId),
    supabase.from("customers").select("id", head).eq("company_id", companyId),
    supabase
      .from("jobs")
      .select("status, invoice_generated, tracking_shared")
      .eq("company_id", companyId)
      .limit(1000),
    supabase.from("quotes").select("id", head).eq("company_id", companyId),
    supabase.from("vehicle_appraisals").select("id", head).eq("company_id", companyId),
    supabase.from("driver_messages").select("id", head).eq("company_id", companyId),
    supabase.from("vehicle_daily_checks").select("id", head).eq("company_id", companyId),
    supabase
      .from("accounting_integrations")
      .select("provider")
      .eq("company_id", companyId)
      .limit(10),
  ]);

  const s: any = settingsRes.data || {};
  const jobs: any[] = (jobsRes.data as any[]) || [];
  const integrations: any[] = (accountingRes.data as any[]) || [];
  const activeIntegration = integrations[0] || null;

  const signals: SetupSignals = {
    hasCompanyName: filled(s.company_name),
    hasCompanyAddress: filled(s.company_address),
    hasContactNumber: filled(s.contact_number),
    hasEmailAddress: filled(s.email_address),
    hasLogo: filled(s.company_logo_url),
    hasVatDecision: s.is_vat_registered === false || filled(s.vat_number),
    hasBankDetails:
      (filled(s.account_name) || filled(s.account_name_enc)) &&
      (filled(s.sort_code) || filled(s.sort_code_enc)) &&
      (filled(s.account_number) || filled(s.account_number_enc)),
    hasPaymentTerms: filled(s.payment_terms),
    accountingConnected: !!activeIntegration,
    accountingProvider: activeIntegration?.provider ?? null,
    driverCount: countOf(driversRes as any),
    vehicleCount: countOf(vehiclesRes as any),
    teamMemberCount: countOf(membersRes as any),
    driverAppDevices: countOf(devicesRes as any),
    customerCount: countOf(customersRes as any),
    jobCount: jobs.length,
    deliveredJobCount: jobs.filter((j) =>
      ["completed", "delivered", "invoiced"].includes(String(j.status)),
    ).length,
    invoiceCount: jobs.filter((j) => j.invoice_generated === true).length,
    quoteCount: countOf(quotesRes as any),
    appraisalCount: countOf(appraisalsRes as any),
    autoInvoicingEnabled: s.auto_invoicing_enabled === true,
    autoEmailInvoice: s.auto_email_invoice === true,
    autoEmailPoc: s.auto_email_poc === true,
    autoEmailPod: s.auto_email_pod === true,
    accountingSyncEnabled:
      s.quickbooks_payment_sync_enabled === true ||
      s.xero_payment_sync_enabled === true ||
      s.freeagent_payment_sync_enabled === true,
    automationTotal: 5,
    automationEnabled: [
      s.auto_invoicing_enabled === true,
      s.auto_email_invoice === true,
      s.auto_email_poc === true,
      s.auto_email_pod === true,
      s.quickbooks_payment_sync_enabled === true ||
        s.xero_payment_sync_enabled === true ||
        s.freeagent_payment_sync_enabled === true,
    ].filter(Boolean).length,
    messageCount: countOf(messagesRes as any),
    dailyCheckCount: countOf(checksRes as any),
    trackingSharedCount: jobs.filter((j) => j.tracking_shared === true).length,
  };

  const preferences: SetupPreferences = {
    enabled: s.onboarding_enabled === true,
    dismissed: s.onboarding_dismissed === true,
    wizardCompleted: s.onboarding_wizard_completed === true,
    tourCompleted: s.onboarding_tour_completed === true,
    milestones: Array.isArray(s.onboarding_milestones) ? (s.onboarding_milestones as string[]) : [],
  };

  return { signals, preferences };
}

/* ------------------------------------------------------------------ */
/* Setup checklist                                                     */
/* ------------------------------------------------------------------ */

export interface SetupTask {
  id: string;
  title: string;
  description: string;
  to: string;
  /** Optional tasks are recommended but do not block completion. */
  optional?: boolean;
  /** 0..1 */
  progress: (s: SetupSignals) => number;
  /** Human readable state, e.g. "2 of 4" or "Not connected". */
  status: (s: SetupSignals) => string;
}

const ratio = (value: number, target: number) => Math.max(0, Math.min(1, value / target));

export const SETUP_TASKS: SetupTask[] = [
  {
    id: "company-setup",
    title: "Company Setup",
    description: "Name, address, contact details, logo and VAT status",
    to: "/settings?tab=company",
    progress: (s) =>
      [s.hasCompanyName, s.hasCompanyAddress, s.hasContactNumber, s.hasEmailAddress, s.hasLogo, s.hasVatDecision]
        .filter(Boolean).length / 6,
    status: (s) => {
      const done = [s.hasCompanyName, s.hasCompanyAddress, s.hasContactNumber, s.hasEmailAddress, s.hasLogo, s.hasVatDecision].filter(Boolean).length;
      return done === 6 ? "Complete" : `${done} of 6`;
    },
  },
  {
    id: "finance-setup",
    title: "Invoice & Payment Settings",
    description: "Bank details and payment terms used on your invoices",
    to: "/settings?tab=invoicing",
    progress: (s) => [s.hasBankDetails, s.hasPaymentTerms].filter(Boolean).length / 2,
    status: (s) => (s.hasBankDetails && s.hasPaymentTerms ? "Complete" : s.hasBankDetails || s.hasPaymentTerms ? "1 of 2" : "Pending"),
  },
  {
    id: "drivers-added",
    title: "Drivers Added",
    description: "Add the drivers who will collect and deliver vehicles",
    to: "/driver-management",
    progress: (s) => ratio(s.driverCount, 2),
    status: (s) => (s.driverCount === 0 ? "Pending" : `${s.driverCount} added`),
  },
  {
    id: "vehicles-added",
    title: "Vehicles Added",
    description: "Register your transporters for daily checks and tracking",
    to: "/vehicles",
    optional: true,
    progress: (s) => ratio(s.vehicleCount, 1),
    status: (s) => (s.vehicleCount === 0 ? "Pending" : `${s.vehicleCount} added`),
  },
  {
    id: "customers-added",
    title: "Customers Added",
    description: "Store billing details so jobs and invoices auto-fill",
    to: "/customers",
    progress: (s) => ratio(s.customerCount, 3),
    status: (s) => (s.customerCount === 0 ? "Pending" : `${s.customerCount} added`),
  },
  {
    id: "accounting-connected",
    title: "Accounting Connected",
    description: "Sync invoices to QuickBooks, Xero, Sage or FreeAgent",
    to: "/settings?tab=invoicing",
    optional: true,
    progress: (s) => (s.accountingConnected ? 1 : 0),
    status: (s) => (s.accountingConnected ? `Connected${s.accountingProvider ? ` — ${s.accountingProvider}` : ""}` : "Not connected"),
  },
  {
    id: "first-job",
    title: "First Job Created",
    description: "Book a vehicle movement with collection and delivery",
    to: "/create-job",
    progress: (s) => (s.jobCount > 0 ? 1 : 0),
    status: (s) => (s.jobCount > 0 ? "Complete" : "Pending"),
  },
  {
    id: "first-delivery",
    title: "First Job Delivered",
    description: "Run the collection and delivery appraisals end to end",
    to: "/jobs",
    optional: true,
    progress: (s) => (s.deliveredJobCount > 0 ? 1 : 0),
    status: (s) => (s.deliveredJobCount > 0 ? "Complete" : "Pending"),
  },
  {
    id: "first-invoice",
    title: "First Invoice Generated",
    description: "Raise an invoice from a completed job",
    to: "/invoicing",
    progress: (s) => (s.invoiceCount > 0 ? 1 : 0),
    status: (s) => (s.invoiceCount > 0 ? "Complete" : "Pending"),
  },
  {
    id: "automation-enabled",
    title: "Automatic Invoicing Enabled",
    description: "Raise the invoice automatically when the driver completes PoD (Company Settings → Invoicing)",
    to: "/settings?tab=invoicing",
    optional: true,
    progress: (s) => (s.autoInvoicingEnabled ? 1 : 0),
    status: (s) => (s.autoInvoicingEnabled ? "Enabled" : "Not enabled"),
  },
  {
    id: "driver-app",
    title: "Driver App Activated",
    description: "Drivers sign in on mobile to receive jobs and send updates",
    to: "/driver-management",
    optional: true,
    progress: (s) => (s.driverAppDevices > 0 ? 1 : 0),
    status: (s) => (s.driverAppDevices > 0 ? `${s.driverAppDevices} device(s)` : "Pending"),
  },
  {
    id: "invite-team",
    title: "Team Invited",
    description: "Add admins or office staff to your company",
    to: "/manage-users",
    optional: true,
    progress: (s) => (s.teamMemberCount > 1 ? 1 : 0),
    status: (s) => (s.teamMemberCount > 1 ? `${s.teamMemberCount} members` : "Pending"),
  },
];

export interface ResolvedSetupTask extends SetupTask {
  value: number;
  complete: boolean;
  statusLabel: string;
}

export const resolveTasks = (s: SetupSignals): ResolvedSetupTask[] =>
  SETUP_TASKS.map((t) => {
    const value = Math.max(0, Math.min(1, t.progress(s)));
    return { ...t, value, complete: value >= 1, statusLabel: t.status(s) };
  });

/* ------------------------------------------------------------------ */
/* Business readiness score                                            */
/* ------------------------------------------------------------------ */

export interface ReadinessCategory {
  id: string;
  label: string;
  score: number; // 0..100
  weight: number;
  tip: string;
  to: string;
}

export interface ReadinessResult {
  score: number; // 0..100 weighted
  categories: ReadinessCategory[];
}

const pct = (v: number) => Math.round(Math.max(0, Math.min(1, v)) * 100);

export function calculateReadiness(s: SetupSignals): ReadinessResult {
  const categories: ReadinessCategory[] = [
    {
      id: "business",
      label: "Business Details",
      weight: 2,
      score: pct(
        [s.hasCompanyName, s.hasCompanyAddress, s.hasContactNumber, s.hasEmailAddress, s.hasLogo, s.hasVatDecision]
          .filter(Boolean).length / 6,
      ),
      tip: "Complete your company name, address, contact details, logo and VAT status in Company Settings.",
      to: "/settings?tab=company",
    },
    {
      id: "fleet",
      label: "Fleet",
      weight: 1,
      score: pct(ratio(s.vehicleCount, 2)),
      tip: "Add your transporters so daily checks, tracking and maintenance warnings work.",
      to: "/vehicles",
    },
    {
      id: "drivers",
      label: "Drivers",
      weight: 2,
      score: pct((ratio(s.driverCount, 2) + (s.driverAppDevices > 0 ? 1 : 0)) / 2),
      tip: "Add drivers and get them signed in on the mobile app to receive jobs.",
      to: "/driver-management",
    },
    {
      id: "customers",
      label: "Customers",
      weight: 2,
      score: pct(ratio(s.customerCount, 3)),
      tip: "Add your regular customers so jobs, quotes and invoices auto-fill their details.",
      to: "/customers",
    },
    {
      id: "finance",
      label: "Finance",
      weight: 2,
      score: pct(
        ([s.hasBankDetails, s.hasPaymentTerms, s.invoiceCount > 0].filter(Boolean).length +
          (s.accountingConnected ? 1 : 0)) / 4,
      ),
      tip: "Add bank details and payment terms, raise your first invoice and connect your accounting package.",
      to: "/settings?tab=invoicing",
    },
    {
      id: "automation",
      label: "Working automatically",
      weight: 1,
      score: pct(s.automationTotal > 0 ? s.automationEnabled / s.automationTotal : 0),
      tip: "Turn on automatic invoicing and appraisal emails in Company Settings — the most popular time-savers.",
      to: "/settings?tab=invoicing",
    },
    {
      id: "communications",
      label: "Communications",
      weight: 1,
      score: pct(((s.trackingSharedCount > 0 ? 1 : 0) + (s.messageCount > 0 ? 1 : 0)) / 2),
      tip: "Share a tracking link with a customer and message a driver from the job screen.",
      to: "/operations",
    },
    {
      id: "compliance",
      label: "Compliance",
      weight: 1,
      score: pct(((s.dailyCheckCount > 0 ? 1 : 0) + (s.appraisalCount > 0 ? 1 : 0)) / 2),
      tip: "Complete a vehicle daily check and a vehicle appraisal to build your audit trail.",
      to: "/vehicle-daily-checks",
    },
  ];

  const totalWeight = categories.reduce((a, c) => a + c.weight, 0);
  const score = Math.round(categories.reduce((a, c) => a + c.score * c.weight, 0) / totalWeight);
  return { score, categories };
}

/* ------------------------------------------------------------------ */
/* Smart recommendations                                               */
/* ------------------------------------------------------------------ */

export interface Recommendation {
  id: string;
  title: string;
  description: string;
  actionLabel: string;
  to: string;
  priority: "high" | "medium" | "low";
}

export function getRecommendations(s: SetupSignals): Recommendation[] {
  const recs: Recommendation[] = [];

  if (!s.hasCompanyName || !s.hasCompanyAddress || !s.hasLogo) {
    recs.push({
      id: "company",
      title: "Finish your company profile",
      description: "Your company details and logo appear on every invoice and appraisal report.",
      actionLabel: "Company Settings",
      to: "/settings?tab=company",
      priority: "high",
    });
  }
  if (s.driverCount === 0) {
    recs.push({
      id: "drivers",
      title: "You have not added any drivers",
      description: "Add drivers so jobs can be assigned and tracked in real time.",
      actionLabel: "Add Driver",
      to: "/driver-management",
      priority: "high",
    });
  }
  if (!s.hasBankDetails || !s.hasPaymentTerms) {
    recs.push({
      id: "finance",
      title: "Add your bank details and payment terms",
      description: "Invoices cannot show payment instructions until these are set.",
      actionLabel: "Invoice Settings",
      to: "/settings?tab=invoicing",
      priority: "high",
    });
  }
  if (s.customerCount === 0) {
    recs.push({
      id: "customers",
      title: "Add your first customer",
      description: "Stored customers speed up job creation and keep invoicing accurate.",
      actionLabel: "Add Customer",
      to: "/customers",
      priority: "high",
    });
  }
  if (s.jobCount === 0) {
    recs.push({
      id: "job",
      title: "Create your first job",
      description: "Book a movement to see tracking, appraisals and invoicing in action.",
      actionLabel: "Create Job",
      to: "/create-job",
      priority: "high",
    });
  }
  if (!s.accountingConnected) {
    recs.push({
      id: "accounting",
      title: "You have not connected an accounting package",
      description: "Sync invoices automatically with QuickBooks, Xero, Sage or FreeAgent.",
      actionLabel: "Connect",
      to: "/settings?tab=invoicing",
      priority: "medium",
    });
  }
  if (!s.autoInvoicingEnabled) {
    recs.push({
      id: "automation",
      title: "Automatic invoicing is not enabled",
      description: "Turn on automatic invoicing in Company Settings so invoices are raised on PoD completion.",
      actionLabel: "Invoice Settings",
      to: "/settings?tab=invoicing",
      priority: "medium",
    });
  }
  if (s.teamMemberCount <= 1) {
    recs.push({
      id: "team",
      title: "You have not invited your team",
      description: "Give office staff and admins their own secure sign-in.",
      actionLabel: "Manage Users",
      to: "/manage-users",
      priority: "low",
    });
  }
  if (s.appraisalCount === 0 && s.jobCount > 0) {
    recs.push({
      id: "appraisal",
      title: "You have not created your first appraisal",
      description: "Photographic condition reports protect you against damage disputes.",
      actionLabel: "View Jobs",
      to: "/jobs",
      priority: "medium",
    });
  }
  if (s.vehicleCount === 0) {
    recs.push({
      id: "vehicles",
      title: "You have not added any vehicles",
      description: "Register transporters to unlock daily checks and fleet visibility.",
      actionLabel: "Add Vehicle",
      to: "/vehicles",
      priority: "low",
    });
  }

  const order = { high: 0, medium: 1, low: 2 };
  return recs.sort((a, b) => order[a.priority] - order[b.priority]);
}

/* ------------------------------------------------------------------ */
/* Feature adoption                                                    */
/* ------------------------------------------------------------------ */

export interface AdoptionFeature {
  id: string;
  label: string;
  used: boolean;
  hint: string;
  to: string;
}

export function getAdoption(s: SetupSignals): AdoptionFeature[] {
  return [
    { id: "tracking", label: "Live Tracking", used: s.trackingSharedCount > 0, hint: "Share a live tracking link with a customer from any job.", to: "/operations" },
    { id: "messaging", label: "Driver Messaging", used: s.messageCount > 0, hint: "Message drivers directly instead of phoning them.", to: "/driver-messages" },
    { id: "automation", label: "Automatic Admin", used: s.autoInvoicingEnabled, hint: "Let TransportMate handle repetitive admin for you.", to: "/settings?tab=invoicing" },
    { id: "analytics", label: "Analytics", used: s.deliveredJobCount > 0, hint: "Review revenue, job volume and driver performance.", to: "/analytics" },
    { id: "driver-app", label: "Driver App", used: s.driverAppDevices > 0, hint: "Drivers get jobs, navigation and appraisals on mobile.", to: "/driver-management" },
    { id: "appraisals", label: "Appraisals", used: s.appraisalCount > 0, hint: "Photographic condition reports at collection and delivery.", to: "/jobs" },
    { id: "quotes", label: "Quotes", used: s.quoteCount > 0, hint: "Send priced quotes and convert them into jobs.", to: "/quote-builder" },
    { id: "daily-checks", label: "Vehicle Daily Checks", used: s.dailyCheckCount > 0, hint: "Digital walkaround checks for compliance records.", to: "/vehicle-daily-checks" },
  ];
}

/* ------------------------------------------------------------------ */
/* Celebration milestones                                              */
/* ------------------------------------------------------------------ */

export interface Milestone {
  id: string;
  title: string;
  message: string;
  reached: (s: SetupSignals) => boolean;
}

export const MILESTONES: Milestone[] = [
  { id: "first-driver", title: "First driver added", message: "Your team is taking shape.", reached: (s) => s.driverCount > 0 },
  { id: "first-vehicle", title: "First vehicle added", message: "Your fleet is now visible across TransportMate.", reached: (s) => s.vehicleCount > 0 },
  { id: "first-customer", title: "First customer added", message: "Jobs and invoices will now auto-fill their details.", reached: (s) => s.customerCount > 0 },
  { id: "first-job", title: "First job created", message: "You're operational.", reached: (s) => s.jobCount > 0 },
  { id: "first-delivery", title: "First job delivered", message: "Nicely done — the full workflow is proven.", reached: (s) => s.deliveredJobCount > 0 },
  { id: "first-invoice", title: "First invoice generated", message: "You're getting paid through TransportMate.", reached: (s) => s.invoiceCount > 0 },
  { id: "accounting-connected", title: "Accounting connected", message: "Invoices will now sync automatically.", reached: (s) => s.accountingConnected },
  { id: "automation-enabled", title: "Working automatically", message: "TransportMate is now working in the background for you.", reached: (s) => s.autoInvoicingEnabled },
];

export const newlyReachedMilestones = (s: SetupSignals, already: string[]): Milestone[] =>
  MILESTONES.filter((m) => m.reached(s) && !already.includes(m.id));
